import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { captureRegion, SCREENGRAB_UI_ATTR, type Rect } from "./captureRegion";
import "@styles/styles-Screengrab.css";

const UI = { [SCREENGRAB_UI_ATTR]: "" }; // spread onto our own nodes so captures skip them

export interface ScreengrabCaptureProps {
  open: boolean;               // parent owns visibility; open=true enters selection
  onClose: () => void;         // fired on cancel AND after onUse — parent flips open=false
  onUse: (blob: Blob) => void; // capture auto-attaches: fires as soon as the grab succeeds
}

const ScreengrabCapture: React.FC<ScreengrabCaptureProps> = ({ open, onClose, onUse }) => {
  const [rect, setRect] = useState<Rect | null>(null);
  const [busy, setBusy] = useState(false);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  // Bumped on every close. An in-flight capture whose result is no longer wanted (user hit
  // Esc) compares its own seq to this and drops its output if it lost the race.
  const captureSeq = useRef(0);

  // Fully controlled: closing is the parent flipping open=false; this effect is the
  // single reset path back to a pristine state for the next open.
  useEffect(() => {
    if (open) return;
    captureSeq.current++;
    startRef.current = null;
    setRect(null);
    setBusy(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const onMouseDown = (e: React.MouseEvent) => {
    if (busy) return;
    startRef.current = { x: e.clientX, y: e.clientY };
    setRect({ x: e.clientX, y: e.clientY, w: 0, h: 0 });
  };

  const onMouseMove = (e: React.MouseEvent) => {
    const s = startRef.current;
    if (!s) return;
    setRect({
      x: Math.min(s.x, e.clientX),
      y: Math.min(s.y, e.clientY),
      w: Math.abs(e.clientX - s.x),
      h: Math.abs(e.clientY - s.y),
    });
  };

  const onMouseUp = async () => {
    const r = rect;
    startRef.current = null;
    if (busy) return;
    if (!r || r.w < 5 || r.h < 5) {
      // Stray click, not a drag — cancel.
      onClose();
      return;
    }
    const seq = captureSeq.current;
    setBusy(true); // rect stays highlighted; centered indicator shows meanwhile
    try {
      const b = await captureRegion(r);
      if (captureSeq.current !== seq) return; // superseded (Esc) — discard
      onUse(b);
      onClose();
    } catch {
      if (captureSeq.current === seq) {
        message.error("Couldn't capture the screenshot.");
        onClose();
      }
    }
  };

  if (!open) return null;

  // Portal to body: the overlay/hint/selection/indicator are all position:fixed, which
  // re-anchors to any ancestor with a transform/filter. Mounted at arbitrary depth (the
  // chat composer), a portal is the only placement that can't silently break.
  return createPortal(
    <>
      <div
        className="screengrab-overlay"
        {...UI}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
      >
        {!busy && (
          <div className="screengrab-hint" {...UI}>
            Drag to select a region · Esc to cancel
          </div>
        )}
        {rect && rect.w > 0 && (
          <div
            className={busy ? "screengrab-selection screengrab-selection--staged" : "screengrab-selection"}
            {...UI}
            style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
          />
        )}
      </div>

      {/* Busy state is a plain element (no antd, no enter animation): html2canvas freezes
          the main thread, so this must paint fully opaque in the one frame captureRegion
          yields before the freeze. Centered on the selected area. */}
      {busy && rect && (
        <div
          className="screengrab-capturing"
          {...UI}
          style={{ left: rect.x + rect.w / 2, top: rect.y + rect.h / 2 }}
        >
          <span className="screengrab-capturing-dot" />
          Capturing…
        </div>
      )}
    </>,
    document.body,
  );
};

export default ScreengrabCapture;
